import { useEffect, useRef, useState, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS - Apple-Level Timing
// ═══════════════════════════════════════════════════════════════════════════════
const ease = [0.16, 1, 0.3, 1]; // Expo out - premium feel
const smoothEase = [0.22, 1, 0.36, 1];
const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };

// ═══════════════════════════════════════════════════════════════════════════════
// COUNT UP HOOK - Smooth Number Animation
// ═══════════════════════════════════════════════════════════════════════════════
export function useCountUp(end, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!startOnView || !inView || hasStarted.current) return;
    hasStarted.current = true;
    const startTime = Date.now();
    const numericEnd = parseFloat(String(end).replace(/[^0-9.]/g, ""));

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Expo out easing for premium feel
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.round(numericEnd * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, end, duration, startOnView]);

  return { count, ref };
}

// ═══════════════════════════════════════════════════════════════════════════════
// FADE IN - Premium Scroll-Triggered Animation
// ═══════════════════════════════════════════════════════════════════════════════
export function FadeIn({ children, className = "", delay = 0, direction = "up", distance = 40 }) {
  const directions = {
    up: { y: distance, x: 0 },
    down: { y: -distance, x: 0 },
    left: { x: distance, y: 0 },
    right: { x: -distance, y: 0 },
    none: { x: 0, y: 0 },
  };
  const d = directions[direction] || directions.up;

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, x: d.x, y: d.y, filter: "blur(4px)" }}
      whileInView={{ opacity: 1, x: 0, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ 
        duration: 1, 
        delay, 
        ease
      }}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEXT REVEAL - Clip Animation
// ═══════════════════════════════════════════════════════════════════════════════
export function TextReveal({ children, className = "", delay = 0 }) {
  return (
    <div className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ 
          duration: 1.1, 
          delay, 
          ease 
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SPLIT TEXT - Letter by Letter Animation
// ═══════════════════════════════════════════════════════════════════════════════
export function SplitText({ children, className = "", delay = 0, staggerDelay = 0.02 }) {
  const text = typeof children === 'string' ? children : '';
  const letters = text.split('');
  
  return (
    <motion.span 
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {letters.map((letter, i) => (
        <motion.span
          key={i}
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: { opacity: 1, y: 0 }
          }}
          transition={{ 
            duration: 0.5, 
            delay: delay + (i * staggerDelay),
            ease: smoothEase
          }}
          style={{ display: 'inline-block' }}
        >
          {letter === ' ' ? '\u00A0' : letter}
        </motion.span>
      ))}
    </motion.span>
  );
}

// Parallax Image with Spring Physics
export function ParallaxImage({ src, alt = "", className = "", speed = 0.3 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const smoothY = useSpring(y, springConfig);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        className="w-full h-[120%] object-cover"
        style={{ y: smoothY }}
      />
    </div>
  );
}

// Magnetic Button Effect
export function MagneticButton({ children, className = "", strength = 0.3 }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const springX = useSpring(x, { stiffness: 150, damping: 15 });
  const springY = useSpring(y, { stiffness: 150, damping: 15 });

  const handleMouseMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;
    x.set(deltaX);
    y.set(deltaY);
  }, [x, y, strength]);

  const handleMouseLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </motion.div>
  );
}

// Hover Card with 3D Tilt Effect
export function TiltCard({ children, className = "", tiltStrength = 10 }) {
  const ref = useRef(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  const handleMouseMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    const rotateXValue = (mouseY / (rect.height / 2)) * -tiltStrength;
    const rotateYValue = (mouseX / (rect.width / 2)) * tiltStrength;
    
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  }, [tiltStrength]);

  const handleMouseLeave = useCallback(() => {
    setRotateX(0);
    setRotateY(0);
  }, []);

  return (
    <motion.div
      ref={ref}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      animate={{ rotateX, rotateY }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{ transformStyle: "preserve-3d", perspective: 1000 }}
    >
      {children}
    </motion.div>
  );
}

// Stagger Container for child animations
export function StaggerContainer({ children, className = "", staggerDelay = 0.1, delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: delay
          }
        }
      }}
    >
      {children}
    </motion.div>
  );
}

// Stagger Item (child of StaggerContainer)
export function StaggerItem({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, ease: smoothEase }
        }
      }}
    >
      {children}
    </motion.div>
  );
}

// Smooth Scroll Progress Indicator
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, springConfig);

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-[#E8A33D] origin-left z-[100]"
      style={{ scaleX }}
    />
  );
}

// Glow Effect on Hover
export function GlowCard({ children, className = "", glowColor = "rgba(221, 107, 32, 0.4)" }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  return (
    <motion.div
      ref={ref}
      className={`relative overflow-hidden ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="pointer-events-none absolute inset-0 z-0"
        animate={{
          opacity: isHovered ? 1 : 0,
          background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, ${glowColor}, transparent 40%)`
        }}
        transition={{ duration: 0.3 }}
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

// Animated Counter Display
export function AnimatedCounter({ value, suffix = "", className = "" }) {
  const { count, ref } = useCountUp(value, 2500);
  
  return (
    <motion.span 
      ref={ref} 
      className={className}
      initial={{ scale: 0.5, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: smoothEase }}
    >
      {count}{suffix}
    </motion.span>
  );
}

// Reveal on Scroll with Blur
export function BlurReveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, filter: "blur(10px)", y: 20 }}
      whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay, ease: smoothEase }}
    >
      {children}
    </motion.div>
  );
}

// Floating Animation
export function Float({ children, className = "", duration = 3, distance = 10 }) {
  return (
    <motion.div
      className={className}
      animate={{ 
        y: [-distance, distance, -distance] 
      }}
      transition={{ 
        duration, 
        repeat: Infinity, 
        ease: "easeInOut" 
      }}
    >
      {children}
    </motion.div>
  );
}

// Pulse Animation
export function Pulse({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      animate={{ 
        scale: [1, 1.05, 1],
        opacity: [0.8, 1, 0.8]
      }}
      transition={{ 
        duration: 2, 
        repeat: Infinity, 
        ease: "easeInOut" 
      }}
    >
      {children}
    </motion.div>
  );
}
